import SceneObject from "../../../lib/services/scene/sceneobject";
import Puppy from "./puppy";
import { PuppyClips } from "./puppyclips";
import JumpGraphService from "../../../lib/services/collision/jump_graph_service";
import {
  JumpAnimKind,
  JumpEdge,
  PuppyLocation,
} from "../../../lib/services/collision/jump_graph_types";

const ARRIVE_BASE_RADIUS = 0.25;
const SHORT_BLEND_IN_SECONDS = 0.12;
const DEFAULT_BLEND_IN_SECONDS = 0.20;
const LONG_BLEND_IN_SECONDS = 0.40;

// Travel tuning
const TRAVEL_SPEED = 2.4;          // units/sec for manual XZ travel
const TRAVEL_SKIP_EPS = 0.03;      // skip travel if remaining XZ <= this
const TRAVEL_ARRIVE_EPS = 0.02;    // snap to land-start when within this XZ
const ANTICIPATE_ANIM = PuppyClips.Crouch_Idle_end;


const LANDING_ANIM = PuppyClips.Crouch_Idle_start;
const LANDED_ANIM = PuppyClips.Crouch_Idle_loop;
const SETTLED_ANIMATION  =  PuppyClips.Crouch_Idle_loop;
const IDLE_ANIMATION = PuppyClips.Sitting_loop_2;
const IDLE_ANIMATION_TRANS_IN = PuppyClips.Sitting_start;
const IDLE_ANIMATION_TRANS_OUT = PuppyClips.Sitting_end;
const IDLE_ANIMATION_DELAY = 1.5;
const DELAY_BETWEEN_JUMPS = 0.25;
const GROUND_DROP_FORWARD = 0.4;

const ARC_HEIGHT = 0.15;       // tune: how high above the higher endpoint
const APEX_U = 0.75;           // tune: u-position of peak (0.5 = symmetric, >0.5 = late peak)



type EdgePlan = {
  edge: JumpEdge;

  source_pos: Float32Array;        // puppy position at edge start (post-walk)
  dest_pos: Float32Array;          // destination node position

  takeoff_end_pos: Float32Array;   // where Jump_start_RM lands the puppy
  land_start_pos: Float32Array;    // where Jump_land_RM should begin

  travel_needed: boolean;
  travel_clip_index: number | null;
};

type State =
  | "idle"
  | "walking_to_base"
  | "next_edge_delay" 
  | "anticipating"   // NEW: Crouch_Idle_start
  | "starting"     // playing Jump_start_RM, using root motion
  | "traveling"    // looping in-place clip, manual XZ + proportional Y
  | "landing"      // playing Jump_land_RM, using root motion
  | "on_object";

export default class JumpController extends SceneObject {
  private puppy: Puppy;
  private graph: JumpGraphService;

  private state: State = "idle";
  private queue: JumpEdge[] = [];
  private location: PuppyLocation = { kind: "ground" };

  private active_plan: EdgePlan | null = null;

  // Press/drag/release re-target tracking
  private active_target_node_id: string | null = null;
  private wants_ground: boolean = false;
  private pending_ground_target: Float32Array | null = null;

  // Helps maybe_replan recognize where the current queue is heading
  private current_destination_node_id: string | null = null;
  private timer:number = 0;
  private idle_on_object:boolean = false;
  

  constructor(key: string, puppy: Puppy, graph: JumpGraphService) {
    super(key);
    this.puppy = puppy;
    this.graph = graph;
  }

  // ---- Public API ----------------------------------------------------------

  get_location(): PuppyLocation {
    return this.location;
  }

  is_busy(): boolean {
    return this.state !== "idle" && this.state !== "on_object";
  }

  /**
   * FrisbeeController should call this every frame while the player is aiming
   * at a cap, and pass null when not aiming at a cap. The controller will
   * pick the new target up at the next safe boundary (after walk-to-base or
   * after a finished edge).
   */
  set_active_target(node_id: string | null): void 
  {
  // Only one intent active at a time.
   if (node_id) this.wants_ground = false;

    this.active_target_node_id = node_id;

    if (node_id && (this.state === "idle" || this.state === "on_object")) {
      this.try_begin_plan_to(node_id);
    }
  }

  set_ground_intent(world_point: Float32Array | null): void {
  if (world_point === null) {
    this.wants_ground = false;
    this.pending_ground_target = null;
    return;
  }

  this.wants_ground = true;
  this.pending_ground_target = new Float32Array([world_point[0], 0, world_point[2]]);
  this.active_target_node_id = null;

  // If we're settled on an object, kick off the descent immediately.
  if (this.state === "on_object" && this.location.kind === "object") {
    this.try_begin_plan_to_ground();
  }
}

private try_begin_plan_to_ground(): void {
  if (this.location.kind !== "object") return;
  const path = this.graph.plan_path(this.location, "ground");
  if (!path || path.length === 0) return;

  this.queue = path.slice();
  this.current_destination_node_id = "ground";
  this.start_next_edge();
}


  /**
   * Optional explicit kickoff if a caller wants to start a path without going
   * through set_active_target. Kept for compatibility with prior wiring.
   */
  begin_path(path: JumpEdge[]): boolean {
    if (path.length === 0) return false;
    this.cancel();
    this.adopt_path(path);
    return true;
  }

  cancel(): void {
    this.queue = [];
    this.active_plan = null;
    this.current_destination_node_id = null;

    if (this.state === "walking_to_base") {
      this.puppy.navigator.clear_goal();
    }
    this.state = this.location.kind === "object" ? "on_object" : "idle";
  }

  // ---- Tick ----------------------------------------------------------------

  tick(delta_time: number): void {
    switch (this.state) {
      case "idle": break;
      case "on_object":
         this.tick_on_object(delta_time);
        break;

      case "walking_to_base":
        this.tick_walking_to_base();
        break;

         case "anticipating":
        this.tick_anticipating(delta_time);
        break;

        case "next_edge_delay":
        this.tick_next_edge_delay(delta_time);
        break;

      case "starting":
        this.tick_starting();
        break;

      case "traveling":
        this.tick_traveling(delta_time);
        break;

      case "landing":
        this.tick_landing();
        break;
    }

    super.tick(delta_time);
  }

  // ---- State handlers ------------------------------------------------------

  private tick_next_edge_delay(delta_time:number)
  {
        this.timer+=delta_time;

        if(this.timer > DELAY_BETWEEN_JUMPS)
        {
              this.start_next_edge();
        }
  }
  private tick_on_object(delta_time:number)
  {
     if(!this.idle_on_object)
     {
        this.timer += delta_time;
        if(this.timer > IDLE_ANIMATION_DELAY)
        {
          const controller = this.puppy.animation_controller_gpu;
          if(controller)
          {
           controller.playSequence([
            { clipIndex: IDLE_ANIMATION_TRANS_IN, blendInSeconds: LONG_BLEND_IN_SECONDS, loop: false },
            { clipIndex: IDLE_ANIMATION, blendInSeconds: DEFAULT_BLEND_IN_SECONDS, loop: true },
            ]);
          }
          this.idle_on_object = true;
        }
     }

  }
  private tick_walking_to_base(): void {
    if (this.puppy.navigator.has_destination()) return;

    // Navigator finished its goal. Pick up any target change before starting.
    this.maybe_replan();

    if (this.queue.length === 0) {
      this.enter_settled();
      return;
    }

    this.start_next_edge();
  }

  private tick_starting(): void {
    this.advance_with_root_motion();

    if (this.has_clip_finished(PuppyClips.Jump_start_RM)) {
      const plan = this.active_plan!;
      if (plan.travel_needed) this.start_travel(plan);
      else this.start_land(plan);
    }
  }

  private tick_anticipating(delta_time:number): void
  {
     this.timer+= delta_time;
     if(this.has_clip_finished(ANTICIPATE_ANIM))
     {
        // Now play Jump_start_RM.
        const controller = this.puppy.animation_controller_gpu;
        if (!controller) { this.cancel(); return; }

        controller.playSequence([
          { clipIndex: PuppyClips.Jump_start_RM, blendInSeconds: SHORT_BLEND_IN_SECONDS, loop: false },
        ]);
      this.state = "starting";
     }
}

private tick_traveling(delta_time: number): void {
  const plan = this.active_plan!;
  const p = this.puppy.transform.position;

  const ax = plan.takeoff_end_pos[0];
  const ay = plan.takeoff_end_pos[1];
  const az = plan.takeoff_end_pos[2];

  const bx = plan.land_start_pos[0];
  const by = plan.land_start_pos[1];
  const bz = plan.land_start_pos[2];

  // Advance XZ at constant speed.
  const dx = bx - p[0];
  const dz = bz - p[2];
  const remaining = Math.hypot(dx, dz);

  if (remaining <= TRAVEL_ARRIVE_EPS) {
    p[0] = bx;
    p[2] = bz;
    p[1] = by;
    this.puppy.transform.update_matrix();
    this.start_land(plan);
    return;
  }

  const step = Math.min(TRAVEL_SPEED * delta_time, remaining);
  const inv = 1 / remaining;
  p[0] += dx * inv * step;
  p[2] += dz * inv * step;

  // Compute u in [0..1] along the entire travel from a to b on XZ.
  const total_xz = Math.hypot(bx - ax, bz - az);
  const progressed_xz = Math.hypot(p[0] - ax, p[2] - az);
  const u = total_xz > 1e-5 ? Math.min(1, progressed_xz / total_xz) : 1;

  // Peak above the higher endpoint, controlled by arc_height.
  // Apex shifted slightly past midpoint so descent feels sharper.
  

  const peak_y = Math.max(ay, by) + ARC_HEIGHT;

  // Skew parabola so apex is at APEX_U instead of 0.5.
  // Use two-piece quadratic: rise from a to peak across [0..APEX_U],
  // fall from peak to b across [APEX_U..1].
  let y;
  if (u <= APEX_U) {
    const r = u / APEX_U; // 0..1 across rise
    // quadratic ease-out so peak is rounded
    const w = 1 - (1 - r) * (1 - r);
    y = ay + (peak_y - ay) * w;
  } else {
    const r = (u - APEX_U) / (1 - APEX_U); // 0..1 across fall
    // quadratic ease-in so descent accelerates
    const w = r * r;
    y = peak_y + (by - peak_y) * w;
  }

  p[1] = y;
  this.puppy.transform.update_matrix();
} 


  private tick_landing(): void {
    this.advance_with_root_motion();

    if (this.has_clip_finished(LANDED_ANIM)) {
      this.snap_to_destination(this.active_plan!);
      this.finish_edge();
    }
  }

  // ---- Edge lifecycle ------------------------------------------------------

  private try_begin_plan_to(node_id: string): void {
    const path = this.graph.plan_path(this.location, node_id);
    if (!path || path.length === 0) return;
    this.adopt_path(path);
  }

  private adopt_path(path: JumpEdge[]): void {
    this.queue = path.slice();
    this.current_destination_node_id = path[path.length - 1].to;

    const first = this.queue[0];

    if (first.from === "ground") {
      this.state = "walking_to_base";
      const dest = this.graph.get_node(first.to);
      if (!dest) {
        this.cancel();
        return;
      }
      const base_target = new Float32Array([dest.position[0], 0, dest.position[2]]);
      this.puppy.navigator.move_to_point(base_target, "aggressive", ARRIVE_BASE_RADIUS);
      return;
    }

    // Already on an object: start jumping immediately.
    this.start_next_edge();
  }

  private start_next_edge(): void {
    if (this.queue.length === 0) {
      this.enter_settled();
      return;
    }

    const edge = this.queue.shift()!;
    const plan = this.build_edge_plan(edge);
    if (!plan) {
      this.cancel();
      return;
    }

    this.active_plan = plan;

    const controller = this.puppy.animation_controller_gpu;
    if (!controller) {
      this.cancel();
      return;
    }

    this.face_toward(plan.dest_pos);

    if(this.idle_on_object)
    {
       controller.playSequence([
        {clipIndex: IDLE_ANIMATION_TRANS_OUT,blendInSeconds: DEFAULT_BLEND_IN_SECONDS,loop: false},
        {clipIndex: ANTICIPATE_ANIM,blendInSeconds: SHORT_BLEND_IN_SECONDS,loop: false},
         ]);
    }
    else
    {
      controller.playSequence([{
          clipIndex: ANTICIPATE_ANIM,blendInSeconds: SHORT_BLEND_IN_SECONDS,loop: false,
        },
      ]);
    }
    this.timer = 0;
    this.state = "anticipating";
    this.idle_on_object = false;
  }

  private start_travel(plan: EdgePlan): void {
    const controller = this.puppy.animation_controller_gpu;
    if (!controller || plan.travel_clip_index === null) {
      this.start_land(plan);
      return;
    }

    controller.playSequence([
      {
        clipIndex: plan.travel_clip_index,
        blendInSeconds: SHORT_BLEND_IN_SECONDS,
        loop: true,
      },
    ]);

    this.state = "traveling";
  }

  private start_land(plan: EdgePlan): void {
    const controller = this.puppy.animation_controller_gpu;
    if (!controller) {
      this.snap_to_destination(plan);
      this.finish_edge();
      return;
    }

    controller.playSequence([
      {
        clipIndex: LANDING_ANIM,
        blendInSeconds: SHORT_BLEND_IN_SECONDS,
        loop: false,
      },
    ]);

    this.state = "landing";
  }

  
  private finish_edge(): void {
  const plan = this.active_plan;
  this.active_plan = null;

  if (!plan) {
    this.maybe_replan();
    this.start_next_edge_or_settle();
    return;
  }

  if (plan.edge.to === "ground") {
    this.location = { kind: "ground" };
    this.queue = [];
    this.current_destination_node_id = null;
    this.wants_ground = false; // intent satisfied

    const tgt = this.pending_ground_target;
    this.pending_ground_target = null;

    if (tgt) {
      // Walk to the spot the player clicked.
      this.puppy.navigator.move_to_point(tgt, "aggressive", 0.25);
      // No JumpController state for ground walking; the navigator handles it
      // and FrisbeeController's existing ground-tracking takes over while held.
    }

    this.state = "idle";
    return;
  }

  this.location = { kind: "object", node_id: plan.edge.to };
  this.maybe_replan();
  this.start_next_edge_or_settle();
}

// Add to JumpController
get_remaining_edge_count(): number {
  // Count current in-flight edge + everything still queued.
  return this.queue.length + (this.active_plan ? 1 : 0);
}

request_ground(world_point: Float32Array): void {
  if (this.location.kind !== "object") return;

  const path = this.graph.plan_path(this.location, "ground");
  if (!path || path.length === 0) return;

  this.active_target_node_id = null;
  this.queue = path.slice();
  this.current_destination_node_id = "ground";
  this.pending_ground_target = new Float32Array([world_point[0], 0, world_point[2]]);

  this.start_next_edge();
}

  private start_next_edge_or_settle(): void {
    if (this.queue.length > 0)
    {
      //we need to delay the start on next edge
      //this.start_next_edge();
      this.state = "next_edge_delay";
      this.timer = 0;
    }
    else 
    {
      this.enter_settled();
    }
  }

  private enter_settled(): void {
    const controller = this.puppy.animation_controller_gpu;
    if (controller) 
    {
      controller.playSequence([
        { clipIndex: SETTLED_ANIMATION, blendInSeconds: DEFAULT_BLEND_IN_SECONDS, loop: true },
      ]);
    }
    this.current_destination_node_id = null;
    this.state = this.location.kind === "object" ? "on_object" : "idle";
    this.timer = 0;
    this.idle_on_object = false;
   
  }

  // ---- Re-targeting --------------------------------------------------------

    private maybe_replan(): void {
  // Ground intent takes precedence if both somehow got set.
  if (this.wants_ground) {
    if (this.location.kind === "ground") {
      // Already there. Nothing to plan.
      this.queue = [];
      this.current_destination_node_id = null;
      return;
    }

    // Build path to ground only if our queue isn't already aiming at ground.
    if (this.current_destination_node_id !== "ground") {
      const path = this.graph.plan_path(this.location, "ground");
      if (!path || path.length === 0) {
        this.queue = [];
        this.current_destination_node_id = null;
        return;
      }
      this.queue = path.slice();
      this.current_destination_node_id = "ground";
    }
    return;
  }

  const target = this.active_target_node_id;

  if (!target) {
    this.queue = [];
    this.current_destination_node_id = null;
    return;
  }

  if (this.current_destination_node_id === target) return;

  if (this.location.kind === "object" && this.location.node_id === target) {
    this.queue = [];
    this.current_destination_node_id = null;
    return;
  }

  const path = this.graph.plan_path(this.location, target);
  if (!path || path.length === 0) {
    this.queue = [];
    this.current_destination_node_id = null;
    return;
  }

  this.queue = path.slice();
  this.current_destination_node_id = path[path.length - 1].to;
}

  // ---- Per-edge plan -------------------------------------------------------

  private build_edge_plan(edge: JumpEdge): EdgePlan | null {
    const dest_node = this.graph.get_node(edge.to);
    if (!dest_node) return null;

    const lib = (this.puppy.animation_controller_gpu as any).library;
    const start_clip = lib.getByIndex(PuppyClips.Jump_start_RM);
    const land_clip = lib.getByIndex(PuppyClips.Jump_land_RM);

    const start_e = start_clip.root_motion_extents;
    const land_e = land_clip.root_motion_extents;

    const p = this.puppy.transform.position;
    const source_pos = new Float32Array([p[0], p[1], p[2]]);

    let dest_pos: Float32Array;
    if (edge.to === "ground") 
    {
     
      const yaw = this.puppy.transform.rotation[1];
      const fwd_x = -Math.sin(yaw);
      const fwd_z =  Math.cos(yaw);
      dest_pos = new Float32Array([
        source_pos[0] + fwd_x * GROUND_DROP_FORWARD,
        0,
        source_pos[2] + fwd_z * GROUND_DROP_FORWARD,
      ]);

    }
    else
     {
      dest_pos = new Float32Array([
        dest_node.position[0],
        dest_node.position[1],
        dest_node.position[2],
      ]);
    }

    // Forward unit vector from source toward destination on XZ.s
    const fx = dest_pos[0] - source_pos[0];
    const fz = dest_pos[2] - source_pos[2];
    const flen = Math.hypot(fx, fz);
    const ux = flen > 1e-5 ? fx / flen : 0;
    const uz = flen > 1e-5 ? fz / flen : 1;

    // Where Jump_start_RM will leave the puppy.
    const takeoff_end_pos = new Float32Array([
      source_pos[0] + ux * start_e.forward_xz,
      source_pos[1] + start_e.vertical_y,
      source_pos[2] + uz * start_e.forward_xz,
    ]);

    // Where Jump_land_RM should begin so it ends exactly at dest_pos.
    const land_start_pos = new Float32Array([
      dest_pos[0] - ux * land_e.forward_xz,
      dest_pos[1] - land_e.vertical_y,
      dest_pos[2] - uz * land_e.forward_xz,
    ]);

    // Manual travel covers the gap between them.
    const tdx = land_start_pos[0] - takeoff_end_pos[0];
    const tdz = land_start_pos[2] - takeoff_end_pos[2];
    const travel_xz = Math.hypot(tdx, tdz);

    const travel_needed = travel_xz > TRAVEL_SKIP_EPS;

    return {
      edge,
      source_pos,
      dest_pos,
      takeoff_end_pos,
      land_start_pos,
      travel_needed,
      travel_clip_index: travel_needed ? this.travel_clip_for_anim(edge.anim) : null,
    };
  }

  private travel_clip_for_anim(anim: JumpAnimKind): number {
    switch (anim) {
      case "ground_to_object":
      case "object_to_object_up":
        return PuppyClips.Jump_up;

      case "object_to_object_across":
        return PuppyClips.Jump_hor;

      case "object_to_object_down_low":
      case "object_to_ground":
        return PuppyClips.Jump_down_low;

      case "object_to_object_down_high":
        return PuppyClips.Jump_down_high;
    }
  }

  // ---- Sequencing helpers --------------------------------------------------

  private has_clip_finished(expected_clip_index: number): boolean {
    const controller = this.puppy.animation_controller_gpu;
    if (!controller) return false;

    const state = controller.get_state();
    const cur = state.currentState;
    if (!cur) return false;

    // Controller may have rolled to a different clip if anything else
    // mutated the sequence; treat that as finished too.
    if (cur.clipIndex !== expected_clip_index) return true;

    return (cur.progress ?? 0) >= 1.0 - 1e-4;
  }

  // ---- Movement helpers ----------------------------------------------------

  private advance_with_root_motion(): void {
    const controller = this.puppy.animation_controller_gpu;
    if (!controller) return;

    const rm = controller.rootMotionDelta.translation;
    const yaw = this.puppy.transform.rotation[1];

    const cosY = Math.cos(yaw);
    const sinY = Math.sin(yaw);

    const worldX = rm[0] * cosY - rm[2] * sinY;
    const worldZ = rm[0] * sinY + rm[2] * cosY;

    this.puppy.transform.position[0] += worldX;
    this.puppy.transform.position[1] += rm[1];
    this.puppy.transform.position[2] += worldZ;

    this.puppy.transform.rotation[1] = this.wrap_pi(
      this.puppy.transform.rotation[1] + controller.rootMotionDelta.rotationY
    );

    this.puppy.transform.update_matrix();
  }

  private snap_to_destination(plan: EdgePlan): void {
    this.puppy.transform.position[0] = plan.dest_pos[0];
    this.puppy.transform.position[1] = plan.dest_pos[1];
    this.puppy.transform.position[2] = plan.dest_pos[2];
    this.puppy.transform.update_matrix();
  }

  private face_toward(target: Float32Array): void {
    const p = this.puppy.transform.position;
    const dx = target[0] - p[0];
    const dz = target[2] - p[2];
    if (Math.abs(dx) < 1e-4 && Math.abs(dz) < 1e-4) return;

    const yaw = Math.atan2(-dx, dz);
    this.puppy.transform.rotation[1] = yaw;
    this.puppy.transform.update_matrix();
  }

  private wrap_pi(angle: number): number {
    while (angle > Math.PI) angle -= Math.PI * 2;
    while (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
  }
}